// Cost centres CRUD (BUILD_SPEC.md §5.1) — Dryer, Hammer Mills, PM1-3, Mobile
// Plant, Site General, etc. per tenant. services/api/src/modules/config/
// cost-centres.ts — create/list/update only (no delete). ADMIN write.
import { useState } from 'react';
import { Alert, Button, Group, Modal, Paper, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCoinOff, IconPlus } from '@tabler/icons-react';
import type { CostCentreCreateInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { EmptyState } from '../../../components/EmptyState';
import { PageHeader } from '../../../components/PageHeader';
import { useIsAdmin } from '../permissions';
import { useCostCentres, useCreateCostCentre, useSites, useUpdateCostCentre } from '../api';
import type { CostCentreDto } from '../types';

interface CostCentreFormValues {
  siteId: string;
  code: string;
  name: string;
}

function toFormValues(defaultSiteId: string, row?: CostCentreDto): CostCentreFormValues {
  return { siteId: row?.siteId ?? defaultSiteId, code: row?.code ?? '', name: row?.name ?? '' };
}

function toInput(values: CostCentreFormValues): CostCentreCreateInput {
  return { siteId: values.siteId, code: values.code.trim(), name: values.name.trim() };
}

function CostCentreFormModal({
  opened,
  onClose,
  editing,
}: {
  opened: boolean;
  onClose: () => void;
  editing: CostCentreDto | null;
}) {
  const { data: sitesPage } = useSites();
  const sites = sitesPage?.items ?? [];
  const create = useCreateCostCentre();
  const update = useUpdateCostCentre(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<CostCentreFormValues>({
    initialValues: toFormValues(sites[0]?.id ?? '', editing ?? undefined),
    validate: {
      siteId: (v) => (v ? null : 'Site is required'),
      code: (v) => (v.trim() ? null : 'Code is required'),
      name: (v) => (v.trim() ? null : 'Name is required'),
    },
  });

  function handleSubmit(values: CostCentreFormValues) {
    mutation.mutate(toInput(values), { onSuccess: () => onClose() });
  }

  const siteOptions = sites.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit cost centre ${editing.code}` : 'New cost centre'} centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save cost centre.'}
            </Alert>
          )}
          <Select label="Site" data={siteOptions} required searchable {...form.getInputProps('siteId')} />
          <TextInput label="Code" required {...form.getInputProps('code')} />
          <TextInput label="Name" placeholder="e.g. Dryer, Hammer Mills, PM1" required {...form.getInputProps('name')} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {editing ? 'Save changes' : 'Create cost centre'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function CostCentreRegister() {
  const isAdmin = useIsAdmin();
  const { data: sitesPage } = useSites();
  const sites = sitesPage?.items ?? [];
  const { data, isLoading, isError } = useCostCentres();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<CostCentreDto | null>(null);
  const rows = data?.items ?? [];
  const siteCode = new Map(sites.map((s) => [s.id, s.code]));

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: CostCentreDto) {
    setEditing(row);
    setModalOpened(true);
  }

  const newCostCentreButton = isAdmin ? (
    <Button leftSection={<IconPlus size={16} />} onClick={openCreate} disabled={sites.length === 0}>
      New cost centre
    </Button>
  ) : undefined;
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26): on an empty
  // register the EmptyState below already offers `newCostCentreButton` as the
  // sole call-to-action — showing it in the page header too rendered it TWICE
  // on a fresh tenant. Reuses the exact emptiness condition the EmptyState
  // branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  return (
    <Stack gap="md">
      <PageHeader
        title="Cost centres"
        description="Set up the cost centres (Dryer, Hammer Mills, PM lines…) that stores issues and consumable spend get charged to."
        actions={showHeaderCreate ? newCostCentreButton : undefined}
      />
      <Text size="sm" c="dimmed">
        A cost centre is a bucket for charging plant spend against, so reports can show where the money went.
      </Text>

      {sites.length === 0 && (
        <Alert color="amber" variant="light">
          Create a site first — cost centres belong to a site.
        </Alert>
      )}

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load cost centres.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Site</Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{siteCode.get(row.siteId) ?? row.siteId}</Table.Td>
                  <Table.Td>{row.code}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>
                    {isAdmin && (
                      <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <EmptyState
                      icon={IconCoinOff}
                      title="No cost centres yet"
                      hint={
                        sites.length === 0
                          ? 'Create a site first — cost centres belong to a site.'
                          : "Add a cost centre for each area that charges stores issues or consumable spend — e.g. Dryer, Hammer Mills, PM1."
                      }
                      action={newCostCentreButton}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <CostCentreFormModal key={editing?.id ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editing={editing} />
    </Stack>
  );
}
